/** Admin Human Resource — people, recruiting, time off, onboarding, performance, pay, documents. All API-backed. */

export type EmploymentType = 'full_time' | 'part_time' | 'contractor' | 'intern';
export type EmploymentStatus = 'active' | 'on_leave' | 'terminated' | 'resigned';

/** Terminated or resigned: no longer with the company. Use this, not a check for one status. */
export const hasLeft = (e: { status: EmploymentStatus }) => e.status === 'terminated' || e.status === 'resigned';

export interface Employee {
  id: string;
  /** Linked FeelyFeely account (`user_account_map.uid`). Null while the account is pending. */
  uid: number | null;
  /** No FeelyFeely account linked yet; link one from their profile once they sign up. */
  accountPending?: boolean;
  name: string;
  email: string;
  phone?: string | null;
  title: string;
  /** Department name, resolved from `departmentId`. */
  department: string;
  departmentId?: string | null;
  levelId?: string | null;
  /** Level name, resolved from `levelId`. */
  level?: string;
  managerId?: string | null;
  employmentType: EmploymentType;
  status: EmploymentStatus;
  location?: string | null;
  avatarColor: string;
  startDate: string;
  endDate?: string | null;
  /** Present on a single-employee read when `uid` is linked. */
  account?: HrLinkedAccount | null;
}

/** The linked platform account, as HR sees it. Console access and staff
 *  status are changed through `/api/admin/users/<uid>/admin|employee`. */
export interface HrLinkedAccount {
  uid: number;
  authid: string;
  name: string;
  email: string;
  username?: string | null;
  consoleAccess: boolean;
  staff: boolean;
  active: boolean;
  roles: string[];
}

/** A staff/console account with no employee record yet (import candidate). */
export interface HrStaffAccount {
  uid: number;
  name: string;
  email: string;
  consoleAccess: boolean;
  staff: boolean;
  active: boolean;
}

export type LevelTrack = 'ic' | 'manager' | 'other';

export interface HrDepartment {
  id: string;
  name: string;
  code: string;
  parentId?: string | null;
  headEmployeeId?: string | null;
  costCenter?: string | null;
  /** Employees in it who have not left (terminated or resigned). */
  headcount: number;
  updatedAt?: string | null;
}

export interface HrLevel {
  id: string;
  code: string;
  name: string;
  rank: number;
  track: LevelTrack;
  status: 'active' | 'retired';
  compensationBand?: { currency: string; minMonthly: number; maxMonthly: number } | null;
  updatedAt?: string | null;
}

export const LEVEL_TRACK_LABEL: Record<LevelTrack, string> = {
  ic: 'Individual contributor',
  manager: 'Manager',
  other: 'Other',
};

/** A colleague as My workplace shows them. */
export interface WorkplacePerson {
  id: string;
  name: string;
  title: string;
  email: string;
  avatarColor: string;
  department: string;
}

/** `GET /api/admin/v2/workplace/me` — always the caller, never anyone else. */
export interface WorkplaceMe {
  uid: number | null;
  employee: Employee | null;
  manager: WorkplacePerson | null;
  directReports: WorkplacePerson[];
  tasks: OnboardingTask[];
}

export interface HrMe {
  uid: number | null;
  employee: Employee | null;
  tasks: OnboardingTask[];
}

/** A leave type's `code` (seeded: vacation, sick, personal, parental, unpaid). */
export type TimeOffType = string;
export type TimeOffStatus = 'pending' | 'approved' | 'denied' | 'cancelled';

/** `hr_leave_type`. `daysPerYear: null` means no cap (unpaid leave). */
export interface LeaveType {
  id: string;
  code: string;
  name: string;
  daysPerYear: number | null;
  paid: boolean;
  status: 'active' | 'retired';
}

export interface TimeOffRequest {
  id: string;
  employeeId: string;
  typeId: string;
  type: TimeOffType;
  typeName: string;
  startDate: string;
  endDate: string;
  /** Working days (Mon-Fri), fixed when filed. */
  days: number;
  status: TimeOffStatus;
  reason?: string;
  requestedAt: string;
  /** Employee id of whoever approved or denied it. */
  decidedBy?: string | null;
  decidedAt?: string | null;
  decisionNote?: string;
}

/** One employee's allowance for one type in one year. Computed on read. */
export interface TimeOffBalance {
  employeeId: string;
  typeId: string;
  type: TimeOffType;
  typeName: string;
  year: number;
  /** null = no cap. */
  allotted: number | null;
  used: number;
  pending: number;
  remaining: number | null;
}

/** `GET /api/admin/v2/workplace/leave`. */
export interface WorkplaceLeave {
  year: number;
  balances: TimeOffBalance[];
  requests: TimeOffRequest[];
  types: LeaveType[];
}

/** `GET /api/admin/v2/workplace/approvals` — the caller's direct reports only. */
export interface WorkplaceApprovals {
  pending: TimeOffRequest[];
  upcoming: TimeOffRequest[];
  /** Expense claims from my direct reports waiting for my decision. */
  expenses?: import('@/client/types/business/spend').Expense[];
}

export type OnboardingCategory = 'paperwork' | 'equipment' | 'access' | 'training' | 'other';
export type ChecklistTaskStatus = 'not_started' | 'in_progress' | 'completed';

export interface OnboardingTask {
  id: string;
  employeeId: string;
  kind: 'onboarding' | 'offboarding';
  title: string;
  category: OnboardingCategory;
  status: ChecklistTaskStatus;
  dueDate?: string;
  assigneeId?: string;
}

export type ReviewCycleStatus = 'upcoming' | 'active' | 'closed';

export interface ReviewCycle {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  /** Reviews are due by this date (shown, not enforced). */
  dueDate?: string | null;
  status: ReviewCycleStatus;
  activatedAt?: string | null;
  closedAt?: string | null;
  stats?: { self: { submitted: number; total: number }; manager: { submitted: number; total: number } };
  /** Returned by activate: how many reviews it opened. */
  opened?: number;
}

/** `GET /api/admin/v2/hr/perf/cycles/<id>`. */
export interface ReviewCycleDetail extends ReviewCycle {
  reviews: PerformanceReview[];
  goals: Goal[];
}

export type GoalStatus = 'not_started' | 'in_progress' | 'at_risk' | 'completed';

export interface Goal {
  id: string;
  employeeId: string;
  cycleId?: string | null;
  title: string;
  description?: string;
  status: GoalStatus;
  progress: number;
  dueDate?: string | null;
  updatedAt?: string | null;
}

export type ReviewType = 'self' | 'manager' | 'peer';
export type ReviewStatus = 'draft' | 'submitted';

export interface PerformanceReview {
  id: string;
  cycleId: string;
  employeeId: string;
  reviewerId: string;
  type: ReviewType;
  /** 1 (well below) to 5 (well above expectations). */
  rating?: number | null;
  strengths?: string;
  improvements?: string;
  status: ReviewStatus;
  submittedAt?: string | null;
}

/** `GET /api/admin/v2/workplace/performance` — always the caller. */
export interface WorkplacePerformance {
  cycles: ReviewCycle[];
  /** About me: my self reviews, plus manager reviews once their cycle closed. */
  reviews: PerformanceReview[];
  /** Manager reviews I owe my reports, in active cycles. */
  toWrite: PerformanceReview[];
  goals: Goal[];
  reportGoals: Goal[];
}

export const RATING_LABEL: Record<number, string> = {
  1: 'Well below expectations',
  2: 'Below expectations',
  3: 'Meets expectations',
  4: 'Exceeds expectations',
  5: 'Well above expectations',
};

export type EmployeeDocumentType = 'contract' | 'id' | 'tax' | 'certification' | 'other';

// ---- Labels & badges -------------------------------------------------------

export const EMPLOYMENT_TYPE_LABEL: Record<EmploymentType, string> = {
  full_time: 'Full-time',
  part_time: 'Part-time',
  contractor: 'Contractor',
  intern: 'Intern',
};

export const EMPLOYMENT_STATUS_LABEL: Record<EmploymentStatus, string> = {
  active: 'Active',
  on_leave: 'On leave',
  terminated: 'Terminated',
  resigned: 'Resigned',
};

export const EMPLOYMENT_STATUS_BADGE: Record<EmploymentStatus, string> = {
  active: 'bg-emerald-50 text-emerald-700',
  on_leave: 'bg-amber-50 text-amber-800',
  terminated: 'bg-gray-100 text-gray-500',
  resigned: 'bg-gray-100 text-gray-500',
};

export const TIME_OFF_STATUS_LABEL: Record<TimeOffStatus, string> = {
  pending: 'Pending',
  approved: 'Approved',
  denied: 'Denied',
  cancelled: 'Cancelled',
};

export const TIME_OFF_STATUS_BADGE: Record<TimeOffStatus, string> = {
  pending: 'bg-amber-50 text-amber-800',
  approved: 'bg-emerald-50 text-emerald-700',
  denied: 'bg-red-50 text-red-700',
  cancelled: 'bg-gray-100 text-gray-500',
};

export const ONBOARDING_CATEGORY_LABEL: Record<OnboardingCategory, string> = {
  paperwork: 'Paperwork',
  equipment: 'Equipment',
  access: 'Access',
  training: 'Training',
  other: 'Other',
};

export const CHECKLIST_STATUS_LABEL: Record<ChecklistTaskStatus, string> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  completed: 'Completed',
};

export const CHECKLIST_STATUS_BADGE: Record<ChecklistTaskStatus, string> = {
  not_started: 'bg-gray-100 text-gray-600',
  in_progress: 'bg-amber-50 text-amber-800',
  completed: 'bg-emerald-50 text-emerald-700',
};

export const REVIEW_CYCLE_STATUS_LABEL: Record<ReviewCycleStatus, string> = {
  upcoming: 'Upcoming',
  active: 'Active',
  closed: 'Closed',
};

export const REVIEW_CYCLE_STATUS_BADGE: Record<ReviewCycleStatus, string> = {
  upcoming: 'bg-sky-50 text-sky-700',
  active: 'bg-emerald-50 text-emerald-700',
  closed: 'bg-gray-100 text-gray-500',
};

export const GOAL_STATUS_LABEL: Record<GoalStatus, string> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  at_risk: 'At risk',
  completed: 'Completed',
};

export const GOAL_STATUS_BADGE: Record<GoalStatus, string> = {
  not_started: 'bg-gray-100 text-gray-600',
  in_progress: 'bg-sky-50 text-sky-700',
  at_risk: 'bg-red-50 text-red-700',
  completed: 'bg-emerald-50 text-emerald-700',
};

export const REVIEW_TYPE_LABEL: Record<ReviewType, string> = {
  self: 'Self',
  manager: 'Manager',
  peer: 'Peer',
};

export const REVIEW_TYPE_BADGE: Record<ReviewType, string> = {
  self: 'bg-sky-50 text-sky-700',
  manager: 'bg-violet-50 text-violet-700',
  peer: 'bg-amber-50 text-amber-800',
};

export const REVIEW_STATUS_LABEL: Record<ReviewStatus, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
};

export const REVIEW_STATUS_BADGE: Record<ReviewStatus, string> = {
  draft: 'bg-gray-100 text-gray-600',
  submitted: 'bg-emerald-50 text-emerald-700',
};

// ------------------------------------------------------------- compensation

export type PayFrequency = 'monthly' | 'annual' | 'hourly';
export type CompReason = 'hire' | 'raise' | 'promotion' | 'adjustment' | 'correction';
export type CompStatus = 'proposed' | 'approved' | 'rejected' | 'cancelled';
export type BonusKind = 'performance' | 'signing' | 'spot' | 'retention' | 'other';
export type BonusStatus = 'proposed' | 'approved' | 'rejected' | 'paid' | 'cancelled';
export type BandPosition = 'below' | 'within' | 'above';

/** One change to base pay. Current pay = latest approved one already in effect. */
export interface CompChange {
  id: string;
  employeeId: string;
  effectiveOn: string;
  amount: number;
  currency: string;
  frequency: PayFrequency;
  reason: CompReason;
  levelId?: string | null;
  reviewId?: string | null;
  status: CompStatus;
  note: string;
  /** Entered directly as existing pay (hire / correction), not proposed. */
  recorded: boolean;
  proposedBy?: number | null;
  decidedBy?: number | null;
  decidedAt?: string | null;
  decisionNote: string;
  createdAt?: string | null;
  /** Returned on a propose/record: where it sits against the level band. */
  band?: BandPosition | null;
}

export interface Bonus {
  id: string;
  employeeId: string;
  kind: BonusKind;
  amount: number;
  currency: string;
  payableOn?: string | null;
  reviewId?: string | null;
  payRunId?: string | null;
  status: BonusStatus;
  note: string;
  proposedBy?: number | null;
  decidedBy?: number | null;
  decidedAt?: string | null;
  decisionNote: string;
  paidAt?: string | null;
  createdAt?: string | null;
}

export interface SalaryView {
  amount: number;
  currency: string;
  frequency: PayFrequency;
  effectiveOn: string;
  /** null for hourly pay. */
  monthly: number | null;
}

export interface CompSummaryRow {
  employeeId: string;
  levelId?: string | null;
  current: SalaryView | null;
  scheduled: SalaryView | null;
  lastChangeOn: string | null;
  band: BandPosition | null;
  pendingChanges: number;
  pendingBonuses: number;
  bonusesThisYear: { amount: number; currency: string }[];
}

export interface CompSummary {
  rows: CompSummaryRow[];
  pending: { changes: CompChange[]; bonuses: Bonus[] };
}

/** One person's pay. In My workplace it holds approved items only. */
export interface CompHistory {
  employeeId: string;
  current: SalaryView | null;
  scheduled: SalaryView | null;
  band: BandPosition | null;
  changes: CompChange[];
  bonuses: Bonus[];
}

export const COMP_REASON_LABEL: Record<CompReason, string> = {
  hire: 'Starting pay',
  raise: 'Raise',
  promotion: 'Promotion',
  adjustment: 'Adjustment',
  correction: 'Correction',
};

export const COMP_STATUS_BADGE: Record<CompStatus | BonusStatus, string> = {
  proposed: 'bg-amber-50 text-amber-800',
  approved: 'bg-emerald-50 text-emerald-700',
  paid: 'bg-sky-50 text-sky-700',
  rejected: 'bg-red-50 text-red-700',
  cancelled: 'bg-gray-100 text-gray-500',
};

export const BONUS_KIND_LABEL: Record<BonusKind, string> = {
  performance: 'Performance',
  signing: 'Signing',
  spot: 'Spot',
  retention: 'Retention',
  other: 'Other',
};

export const FREQUENCY_LABEL: Record<PayFrequency, string> = {
  monthly: 'per month',
  annual: 'per year',
  hourly: 'per hour',
};

export const BAND_BADGE: Record<BandPosition, { label: string; className: string }> = {
  below: { label: 'Below band', className: 'bg-amber-50 text-amber-800' },
  within: { label: 'In band', className: 'bg-emerald-50 text-emerald-700' },
  above: { label: 'Above band', className: 'bg-violet-50 text-violet-700' },
};

// ------------------------------------------------------------------ payroll

export interface PayLine {
  code: string;
  label: string;
  amount: number;
  note?: string;
  taxable?: number;
  bonusId?: string;
}

export interface Payslip {
  id: string;
  payRunId: string;
  employeeId: string;
  currency: string;
  gross: number;
  deductions: number;
  net: number;
  employerCost: number;
  lines: { earnings: PayLine[]; deductions: PayLine[]; employer: PayLine[]; bonusFlat?: number };
  /** e.g. 'no_tax_rules' — what could not be worked out. */
  flags: string[];
  /** Present in My workplace. */
  period?: string;
  payDate?: string | null;
  runStatus?: PayRunStatus;
}

export type PayRunStatus = 'draft' | 'approved' | 'paid';

export interface PayRun {
  id: string;
  /** YYYY-MM */
  period: string;
  payDate?: string | null;
  status: PayRunStatus;
  preparedBy?: number | null;
  calculatedAt?: string | null;
  approvedBy?: number | null;
  approvedAt?: string | null;
  paidAt?: string | null;
  notes: string;
  totals?: { currency: string; gross: number; deductions: number; net: number; employerCost: number; people: number }[];
  flagged?: number;
  payslips?: Payslip[];
  skipped?: { employeeId: string; reason: 'no_pay_recorded' | 'hourly_pay' }[];
  ratesInForce?: boolean;
}

export interface StatutoryRateSet {
  id: string;
  country: string;
  effectiveFrom: string;
  source: string;
  rates: {
    currency: string;
    paye_bands: [number | null, number][];
    ssnit_employee_pct: number;
    ssnit_employer_pct: number;
    ssnit_cap_monthly: number;
    bonus_flat_pct: number;
    bonus_cap_pct_of_annual_basic: number;
  };
}

export const PAY_RUN_BADGE: Record<PayRunStatus, string> = {
  draft: 'bg-gray-100 text-gray-700',
  approved: 'bg-sky-50 text-sky-700',
  paid: 'bg-emerald-50 text-emerald-700',
};

// ------------------------------------------- phase 9: policies, documents, hiring

export type HrPolicyCategory = 'conduct' | 'leave' | 'expenses' | 'security' | 'it' | 'health_safety' | 'other';

export interface HrPolicy {
  id: string;
  title: string;
  category: HrPolicyCategory;
  body: string;
  version: number;
  status: 'draft' | 'published' | 'archived';
  requiresAck: boolean;
  publishedAt?: string | null;
  updatedAt?: string | null;
  acknowledged?: number;
  toAcknowledge?: number;
  acknowledgedByMe?: boolean;
}

export const HR_POLICY_CATEGORY_LABEL: Record<HrPolicyCategory, string> = {
  conduct: 'Code of conduct',
  leave: 'Leave',
  expenses: 'Expenses',
  security: 'Security',
  it: 'IT & devices',
  health_safety: 'Health & safety',
  other: 'Other',
};

export type HrDocumentKind = 'contract' | 'offer' | 'id' | 'tax' | 'certification' | 'other';

export interface HrDocument {
  id: string;
  employeeId: string;
  kind: HrDocumentKind;
  name: string;
  contentType?: string | null;
  visibleToEmployee: boolean;
  expiresOn?: string | null;
  uploadedAt?: string | null;
}

export const HR_DOCUMENT_KIND_LABEL: Record<HrDocumentKind, string> = {
  contract: 'Contract',
  offer: 'Offer letter',
  id: 'ID',
  tax: 'Tax',
  certification: 'Certification',
  other: 'Other',
};

export type CandidateStageV2 = 'applied' | 'screen' | 'interview' | 'offer' | 'hired' | 'rejected';
export const CANDIDATE_STAGES: CandidateStageV2[] = ['applied', 'screen', 'interview', 'offer', 'hired', 'rejected'];
export const CANDIDATE_STAGE_LABEL: Record<CandidateStageV2, string> = {
  applied: 'Applied',
  screen: 'Screening',
  interview: 'Interview',
  offer: 'Offer',
  hired: 'Hired',
  rejected: 'Rejected',
};

export interface JobPostingV2 {
  id: string;
  title: string;
  departmentId?: string | null;
  hiringManagerId?: string | null;
  location: string;
  employmentType: EmploymentType;
  status: 'draft' | 'open' | 'on_hold' | 'closed';
  openings: number;
  description: string;
  postedAt?: string | null;
  pipeline?: Record<CandidateStageV2, number>;
}

export interface CandidateV2 {
  id: string;
  jobPostingId: string;
  name: string;
  email: string;
  phone: string;
  source: string;
  stage: CandidateStageV2;
  rating?: number | null;
  notes: string;
  hasCv: boolean;
  employeeId?: string | null;
  appliedAt?: string | null;
}
